/**
 * SettingStepper - Labeled −/value/+ stepper for the probability panel settings
 *
 * Shared by the keep-rule thresholds and the turn-simulation options so both
 * bars stay visually and behaviourally identical.
 */
import type { ReactElement } from 'react';
import { IoAdd, IoRemove } from 'react-icons/io5';

// ============================================================================
// Types
// ============================================================================

export interface SettingStepperProps {
  label: string;
  hint: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function SettingStepper({ label, hint, value, min, max, onChange }: SettingStepperProps): ReactElement {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-xs text-zinc-300">{label}</div>
        <div className="text-ash/70 truncate text-[10px]">{hint}</div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={() => onChange(value - 1)}
          disabled={value <= min}
          className="flex h-6 w-6 items-center justify-center rounded bg-zinc-800 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Decrease ${label}`}
        >
          <IoRemove className="h-3 w-3" />
        </button>
        <span className="text-pearl w-6 text-center text-sm font-medium tabular-nums" aria-live="polite">
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          disabled={value >= max}
          className="flex h-6 w-6 items-center justify-center rounded bg-zinc-800 text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-zinc-100 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={`Increase ${label}`}
        >
          <IoAdd className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
